import React, { useEffect, useRef } from 'react';
import yobstiLogo from '@/public/img/logo.png';
import close from '@/public/svg/close.svg';
import daechlis from '@/public/svg/daechlis.svg';
import foodMarket from '@/public/svg/food-market.svg';
import { track } from '@/helpers/analytics';

interface PressReleaseDialogProps {
    show: boolean;
    onClose: () => void;
}

const PressReleaseDialog: React.FC<PressReleaseDialogProps> = ({ show, onClose }) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        closeButtonRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKey);
        };
    }, [show, onClose]);

    if (!show) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="press-release-title"
                className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white text-textColor shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    ref={closeButtonRef}
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 z-10 rounded-full bg-white p-2 shadow hover:bg-gray-100"
                    aria-label="Schließen"
                >
                    <img src={close} alt="" className="h-5 w-5" />
                </button>

                <img src={daechlis} alt="" className="w-full h-auto" />

                <div className="p-6 sm:px-10 sm:pb-10 sm:pt-6">
                <div className="mb-6 flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-secondaryLight px-3 py-1 text-xs font-bold uppercase tracking-wide">
                        <img src={foodMarket} alt="" className="h-4 w-4" />
                        Presseinformation
                    </span>
                    <img src={yobstiLogo} alt="Yobsti" className="h-9" />
                </div>

                <h2 id="press-release-title" className="mb-4 text-2xl font-bold sm:text-3xl">
                    Aus teo wird Yobsti: Mannheimer Start-up will 35 Märkte übernehmen
                </h2>
                <p className="mb-6 rounded-2xl bg-primaryLight/50 p-4 font-medium">
                    Heute vier Läden, künftig 39: Das Mannheimer Start-up Yobsti will mit eigener Software
                    den Betrieb vereinfachen – und damit langfristig auch günstige Preise ermöglichen.
                </p>

                <div className="space-y-4 leading-relaxed">
                    <p>
                        Mannheim. Noch schnell Milch holen, etwas fürs Abendessen besorgen oder die vergessene
                        Butter kaufen: Für viele Menschen gehört der kleine teo-Markt längst zum Alltag. An 35
                        Standorten soll künftig ein neuer Name über der Tür stehen: Yobsti.
                    </p>
                    <p>
                        Das Mannheimer Start-up plant, die Märkte zum 1. November 2026 von EDEKA zu übernehmen.
                        Aus bislang vier eigenen Läden würden damit auf einen Schlag 39. Hinter dem Wachstum steht
                        ein Team, das selbst Waren ausgefahren, Regale eingeräumt und seine ersten Pilotmärkte
                        aufgebaut hat.
                    </p>

                    <h3 className="flex items-center gap-2 pt-2 text-lg font-bold"><span className="h-3 w-3 rounded-full bg-primary" aria-hidden="true" />Neue Marke, vertrauter Einkauf</h3>
                    <p>
                        Nach einer kurzen Schließung für die Umstellung sollen die Märkte möglichst am 6. November
                        um 12 Uhr wieder öffnen. Einzelne Standorte können davon abweichen. Geplant sind künftig
                        tägliche Öffnungszeiten von 6 bis 22 Uhr.
                    </p>
                    <p>
                        Das Einkaufsprinzip bleibt einfach: reingehen, Produkte auswählen, selbst scannen und
                        bezahlen. Für den Zutritt ist eine Bankkarte vorgesehen; auch digitale Bankkarten über Apple
                        Pay und Google Pay sollen funktionieren. Eine App wird dafür nicht benötigt.
                    </p>
                    <p>
                        Mit der Belieferung durch EDEKA Foodservice zieht schrittweise auch die EDEKA-Eigenmarke
                        GUT &amp; GÜNSTIG ein. Später sollen regionale Lieferanten das Angebot ergänzen – besonders in
                        ländlichen Orten. Yobsti will die Nahversorgung stärken und jeden Markt nach und nach auf
                        seine Nachbarschaft ausrichten.
                    </p>

                    <h3 className="flex items-center gap-2 pt-2 text-lg font-bold"><span className="h-3 w-3 rounded-full bg-primary" aria-hidden="true" />Die Technik arbeitet im Hintergrund</h3>
                    <p>
                        Während Kunden ihren Einkauf erledigen, entwickelt das verbundene Unternehmen Yobst die
                        Software hinter den Märkten weiter. Sie soll Informationen zusammenbringen und dem Team
                        zeitaufwendige Abstimmung abnehmen. Was dadurch an Aufwand und Kosten gespart wird, soll
                        künftig auch den Kunden zugutekommen: Yobsti will so Spielraum für günstigere Preise schaffen.
                    </p>
                    <blockquote className="relative rounded-2xl bg-tertiaryLight px-5 py-4 text-lg font-semibold">
                        „Was wir in einem Laden lernen, soll auch dem nächsten helfen“
                        <span className="mt-1 block text-sm font-normal">Andre Tiede</span>
                        <span className="absolute -bottom-2 left-8 h-4 w-4 rotate-45 bg-tertiaryLight" aria-hidden="true" />
                    </blockquote>
                    <p>
                        Zunächst zählt vor allem eines: die Türen wieder öffnen, damit die Menschen ihren Einkauf in
                        der Nähe erledigen können. Wann welcher Markt startet, veröffentlicht Yobsti auf yobsti.de und
                        seinen Social-Media-Kanälen.
                    </p>

                    <div className="mt-2 rounded-2xl border-2 border-dashed border-secondary p-4">
                    <h3 className="mb-1 font-bold">Pressekontakt</h3>
                    <p className="text-sm">
                        Konrad Claus · Presse und Kommunikation
                        <br />
                        Yobsti Nahversorgung GmbH
                        <br />
                        <a href="mailto:presse@yobsti.de" onClick={() => track('press-mail-click')} className="underline underline-offset-2 hover:text-primaryDark">
                            presse@yobsti.de
                        </a>
                    </p>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
};

export default PressReleaseDialog;
